// src/composables/useRentalFilters.js
import { ref, computed } from 'vue';

// Status em que a próxima ação é a entrega/retirada (usa delivery_date)
const WAITING_DELIVERY_STATUSES = ['PENDING_DELIVERY', 'PENDING_PICKUP'];
// Status em que a próxima ação é a coleta/devolução (usa pickup_date) —
// inclui ACTIVE: locação em andamento, a próxima ação é ir buscar.
const WAITING_PICKUP_STATUSES = ['ACTIVE', 'PENDING_COMPANY_PICKUP', 'PENDING_RETURN'];

// Data da próxima ação da locação, de acordo com o status atual.
// null quando não há próxima ação (ex: COMPLETED).
export function nextActionDate(item) {
  if (WAITING_DELIVERY_STATUSES.includes(item.status)) return item.delivery_date;
  if (WAITING_PICKUP_STATUSES.includes(item.status)) return item.pickup_date;
  return null;
}

// period: '' (todos) | 'today' | 'week' (semana atual, segunda a domingo).
// Sem data (ex: locação encerrada) só combina com period === ''.
function isInPeriod(dateStr, period) {
  if (!period) return true;
  if (!dateStr) return false;

  const date = new Date(dateStr);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === 'today') {
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);
    return date >= startOfToday && date < endOfToday;
  }

  if (period === 'week') {
    const daysSinceMonday = (startOfToday.getDay() + 6) % 7; // getDay(): 0=domingo
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - daysSinceMonday);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 7);
    return date >= startOfWeek && date < endOfWeek;
  }

  return true;
}

export function useRentalFilters(sourceRef) {
  const filters = ref({
    status: '',
    city: '',
    client: '',
    period: '' // '' | 'today' | 'week' — filtra pela data da próxima ação (entrega/coleta)
  });

  const availableStatuses = computed(() => {
    const set = new Set(sourceRef.value.map(item => item.status).filter(Boolean));
    return Array.from(set);
  });

  const filteredList = computed(() => {
    const { status, city, client, period } = filters.value;
    return sourceRef.value
      .filter(item => {
        const matchesStatus = !status || item.status === status;
        const matchesCity = !city || (item.city || '').toLowerCase().includes(city.toLowerCase());
        const matchesClient = !client || (item.client?.name || '').toLowerCase().includes(client.toLowerCase());
        const matchesPeriod = isInPeriod(nextActionDate(item), period);
        return matchesStatus && matchesCity && matchesClient && matchesPeriod;
      })
      // Data da próxima ação mais próxima primeiro; sem próxima ação (encerradas) vai pro fim.
      .sort((a, b) => {
        const dateA = nextActionDate(a);
        const dateB = nextActionDate(b);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA) - new Date(dateB);
      });
  });

  function resetFilters() {
    filters.value = { status: '', city: '', client: '', period: '' };
  }

  return { filters, filteredList, availableStatuses, resetFilters, nextActionDate };
}
